<template>
    <div>
        <v-row>
            <v-col>
                <h4 class="text-h4">Debit Note</h4>
                <p class="text-subtitle">Debit Note Display</p>
                <v-divider></v-divider>
            </v-col>
        </v-row>
        <div class="text-center d-flex flex-column justify-center align-center " style="height: 100%" v-if="loading.data">
            <v-progress-linear
                height="4"
                indeterminate>
            </v-progress-linear>
            <div class="text-body-1 pa-2">Please Wait ...</div>
        </div>
        <div v-else>
            <v-row>
                <v-col cols="12" md="3">
                    <v-btn block color="primary" @click="simulate" :loading="loading.simulate">
                        <v-icon left>mdi-form-select</v-icon>
                        Simulate
                    </v-btn>
                </v-col>
                <v-col cols="12" md="3">
                    <v-btn block outlined disabled>
                        <v-icon left>mdi-printer</v-icon>
                        Print
                    </v-btn>
                </v-col>
            </v-row>
            <v-alert text type="info" v-if="loading.simulate" dense v-html="aicaSimulatingAlert" class="my-1"></v-alert>
            <v-row>
                <v-col cols="12" md="6">
                    <v-subheader>Generate Information</v-subheader>
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>Code</th>
                                <td>{{ debitNoteData.code }}</td>
                            </tr>
                            <tr>
                                <th>Date</th>
                                <td>
                                    {{ formatTimestamp(debitNoteData.created_at) }}
                                </td>
                            </tr>
                            <tr>
                                <th>Document Date</th>
                                <td>{{ debitNoteData.date }}</td>
                            </tr>
                            <tr>
                                <th>State</th>
                                <td>
                                    <p-status :state="debitNoteData.state"></p-status>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
                <v-col cols="12" md="6">
                    <v-subheader>Customer Information</v-subheader>
                    <document-customer-preview :id="debitNoteData.customer_id" v-if="debitNoteData.customer_id"></document-customer-preview>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-simple-table dense class="text-body-1">
                        <thead>
                            <tr>
                                <th>Description</th>
                                <th>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{{ debitNoteData.notes }}</td>
                                <td>{{ formatNumber(debitNoteData.amount, 2) }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
            </v-row>
            <v-row class="mt-2">
                <v-col cols="12">
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>Created By</th>
                                <td>{{debitNoteData.created_by}}</td>
                                <th>Created At</th>
                                <td>{{formatTimestamp(debitNoteData.created_at)}}</td>
                            </tr>         
                            <tr>
                                <th>Updated By</th>
                                <td>{{debitNoteData.updated_by}}</td>
                                <th>Updated At</th>
                                <td>{{formatTimestamp(debitNoteData.updated_at)}}</td>
                            </tr>   
                            <tr v-if="isPosted">
                                <th>Posted By</th>
                                <td>{{debitNoteData.posted_by}}</td>
                                <th>Posted At</th>
                                <td>{{formatTimestamp(debitNoteData.posted_at)}}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
            </v-row>
        </div>
        <!-- TODO: can post to be changed with the real permissions -->
        <simulate-entries
            v-if="simulateEntryShown" 
            v-model="simulateEntryShown" 
            :entry="entry" 
            :is-posted="isPosted" 
            :can-post="true"
            @post="post"
            :is-posting="loading.post">
        </simulate-entries>
        <AlertDialog 
            ref="alertDialog"/>
    </div>
</template>

<script>
import moment from "moment";
import { formatNumber } from 'accounting';

import { aicaSimulatingAlert, aicaPostedAlert } from "../../assets/text/alerts";
import { getDpk } from "../../services/sales";
import { getDebitNote, postDebitNote, simulateDebitNote } from "../../services/sales/debitNote";

import DocumentCustomerPreview from "../../components/Sales/DocumentCustomerPreview";

import SimulateEntries from "../../components/accounting/SimulateEntries.vue";

import PStatus from '../../components/common/PStatus';
import AlertDialog from '@/components/common/AlertDialog';

// import LoadingDialog

export default {
    name: "DebitNoteDisplayView",
    components: { DocumentCustomerPreview, SimulateEntries, PStatus, AlertDialog },
    data() {
        return {
            debitNoteData: {},
            loading: {
                data: false,
                simulate: false,
                post: false
            },
            entry: {},
            simulateEntryShown: false,
            aicaSimulatingAlert,
            aicaPostedAlert,
            dpk: null,
            id: null
        };
    },
    computed: {
        isPosted() {
            return this.debitNoteData.is_posted;
        }
    },
    methods: {
        formatNumber,
        formatTimestamp(timestamp) {
            return moment(timestamp).format("YYYY-MM-DD");
        },
        fetchDebitNote() {
            this.loading.data = true;
            getDebitNote(this.id)
                .then((response) => {
                    this.debitNoteData = response.data;
                })
                .finally(() => (this.loading.data = false));
        },
        simulate() {
            this.loading.simulate = true;
            simulateDebitNote({ debitNoteId: this.debitNoteData.id })
                .then((response) => {
                    this.entry = response.data;
                    this.simulateEntryShown = true;
                }).catch((error) => {
                    this.loading.simulate = false;
                    let message = error.response ? error.response.data.message : error;
                    let errors = error.response ? error.response.data.errors : [];
                    message += '\n';
                    message += errors.join();
                    this.$refs.alertDialog.open({type: "error", message});
                }).finally(() => (this.loading.simulate = false));
        },
        post() {
            this.loading.post = true;
            postDebitNote({ debitNoteId: this.debitNoteData.id, dpk: this.dpk })
                .then(response => {
                    this.debitNoteData = response.data.record;
                    this.simulateEntryShown = false;
                    this.$emit("updated");
                }).catch((error) => {
                    this.loading.simulate = false;
                    let message = error.response ? error.response.data.message : error;
                    let errors = error.response ? error.response.data.errors : [];
                    message += '\n';
                    message += errors.join();
                    this.$refs.alertDialog.open({type: "error", message});
                }).finally(() => {
                    this.loading.post = false;
                });
        },
        fetchDpk() {
            getDpk().then((response) => (this.dpk = response.data.key));
        },
    },
    created() {
        this.id = this.$route.params.id;
        this.fetchDebitNote();
        this.fetchDpk();
    },
};
</script>
